import { useCallback } from 'react'
import type { BuildPartDto } from '../../app/models/build'
import type { ZoneRect } from './useZoneGeometry'
import { getPartDefaults } from './partDefaults'
import PartOverlayItem from './PartOverlayItem'

interface Props {
  buildParts: BuildPartDto[];
  zoneGeometry: Record<string, ZoneRect>;
  /** Container width/height in pixels — used to convert normalised coords to px. */
  containerWidth: number;
  containerHeight: number;
  platform: string | null | undefined;
  isOwner: boolean;
  activeZone: string | null;
  onPartPositionChange: (buildPartId: string, x: number, y: number) => void;
  onPartPositionReset: (buildPartId: string) => void;
}

/**
 * Absolute-positioned overlay rendered on top of PlatformSchematic.
 * Each BuildPart gets a draggable icon pinned at its saved or default position.
 *
 * Positions are fractions of the full schematic container (0–1 × 0–1),
 * NOT fractions of the individual zone rect.  This matches the values stored
 * in XOffsetPercent / YOffsetPercent and the entries in PART_DEFAULTS.
 */
export default function PartOverlayLayer ({
  buildParts,
  zoneGeometry,
  containerWidth,
  containerHeight,
  platform,
  isOwner,
  activeZone,
  onPartPositionChange,
  onPartPositionReset
}: Props) {
  // Convert container-fraction coords → pixel coords.
  const toPixels = useCallback(
    (xFrac: number, yFrac: number) => ({
      x: xFrac * containerWidth,
      y: yFrac * containerHeight
    }),
    [containerWidth, containerHeight]
  )

  // Convert pixel coords back to container-fraction coords.
  const fromPixels = useCallback(
    (px: number, py: number) => ({
      x: Math.max(0, Math.min(1, px / containerWidth)),
      y: Math.max(0, Math.min(1, py / containerHeight))
    }),
    [containerWidth, containerHeight]
  )

  if (containerWidth === 0 || Object.keys(zoneGeometry).length === 0) return null

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none', // children that need interaction set their own
        zIndex: 5
      }}
    >
      {buildParts.map((bp) => {
        const zone = bp.zoneOverride ?? bp.part?.category ?? 'Other'
        const category = bp.part?.category ?? 'Other'
        const defaults = getPartDefaults(platform, category)

        // Use saved position if present; fall back to baked default.
        const xFrac = bp.xOffsetPercent ?? defaults.position.x
        const yFrac = bp.yOffsetPercent ?? defaults.position.y

        const position = toPixels(xFrac, yFrac)

        const handleDragEnd = (px: number, py: number) => {
          const frac = fromPixels(px, py)
          onPartPositionChange(bp.id, frac.x, frac.y)
        }

        return (
          <PartOverlayItem
            key={bp.id}
            buildPart={bp}
            position={position}
            size={defaults.size}
            isOwner={isOwner}
            isActiveZone={zone === activeZone}
            onDragEnd={handleDragEnd}
            onResetPosition={() => onPartPositionReset(bp.id)}
          />
        )
      })}
    </div>
  )
}
